import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout, FieldLabel } from '@/components/AuthLayout';
import { resetMessage, resetPassword, type ResetOutcome } from '@/lib/auth';

/** Matches the API's own floor, so the rule is stated before it is enforced. */
const MIN_LENGTH = 12;

/**
 * The third reset screen, which the design does not have.
 *
 * Built from the same AuthLayout as the other two rather than invented: the
 * person arriving here has just come from an email, and a screen that does not
 * look like the one they left is the point at which a reset link starts to look
 * like a phishing page.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;

    /*
     * Checked here, and again on the server. This one exists to save a round
     * trip and to name the field; the server's is the one that actually holds,
     * because nothing stops a caller skipping this screen entirely.
     */
    if (password !== confirm) {
      setFailure('The two passwords do not match.');
      return;
    }
    if (password.length < MIN_LENGTH) {
      setFailure(`Choose a password of at least ${MIN_LENGTH} characters.`);
      return;
    }

    setPending(true);
    setFailure(null);
    const outcome: ResetOutcome = await resetPassword(token, password);
    setPending(false);

    if (outcome === 'done') {
      navigate('/login', { replace: true, state: { passwordWasReset: true } });
      return;
    }
    setFailure(resetMessage(outcome));
  };

  /*
   * No token at all means the link was truncated on its way through a mail
   * client, or somebody opened this page directly. Offering the form would only
   * produce a failure after they had typed a password twice.
   */
  if (!token) {
    return (
      <AuthLayout
        title="This link is not complete"
        subtitle="The reset link is missing its token — it may have been cut short by your email app."
        footnote="Reset links work once and expire after 30 minutes."
      >
        <Link
          to="/forgot-password"
          className="btn-accent focus-ring flex min-h-[42px] items-center justify-center p-[11px] text-sm"
        >
          Ask for a new link
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="This ends every other session signed in as you."
      footnote={`At least ${MIN_LENGTH} characters. A passphrase is easier to remember and harder to guess.`}
    >
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <label className="flex flex-col gap-[7px]">
          <FieldLabel>New password</FieldLabel>
          <input
            type="password"
            name="password"
            required
            minLength={MIN_LENGTH}
            maxLength={256}
            autoComplete="new-password"
            placeholder="••••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field field-auth"
          />
        </label>

        <label className="flex flex-col gap-[7px]">
          <FieldLabel>Repeat new password</FieldLabel>
          <input
            type="password"
            name="confirm"
            required
            maxLength={256}
            autoComplete="new-password"
            placeholder="••••••••••••"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="field field-auth"
          />
        </label>

        {failure ? (
          <p
            role="alert"
            className="rounded-md px-3 py-2.5 text-[13px]"
            style={{
              color: 'var(--text)',
              background: 'color-mix(in oklab, var(--danger, oklch(0.62 0.19 25)) 12%, transparent)',
              border: '1px solid color-mix(in oklab, var(--danger, oklch(0.62 0.19 25)) 35%, transparent)',
            }}
          >
            {failure}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={pending}
          className="btn-accent focus-ring min-h-[42px] cursor-pointer p-[11px] text-sm disabled:cursor-wait disabled:opacity-70"
        >
          {pending ? 'Saving…' : 'Set new password'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="btn-quiet focus-ring min-h-[32px] cursor-pointer p-1 text-[13.5px]"
        >
          ← Back to sign in
        </button>
      </form>
    </AuthLayout>
  );
}
